import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock,
  Compass,
  Ear,
  Flame,
  Gauge,
} from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { ConversionBand } from "@/components/site/ConversionBand";
import { Reveal } from "@/hooks/useReveal";
import {
  BUILT_INDEX_ROUTES,
  BUILT_PRINCIPLES,
  FULL_REPORT_PRICE_USD,
  ORIGINAL_REPORT_PRICE_USD,
} from "@/content/built-index";

const ASSESSMENTS = [
  {
    icon: Gauge,
    name: "The Built Diagnostic™",
    detail:
      "Score all 10 MAKECHANGE principles out of 100 and see exactly where your leadership structure is paying a tax.",
  },
  {
    icon: Compass,
    name: "Leadership Style (DISC)",
    detail:
      "Identify how you actually decide, drive and delegate under pressure — plus the built move that offsets your blind spot.",
  },
  {
    icon: Flame,
    name: "Fuel & Drain",
    detail:
      "Find what refuels you and what quietly burns you out, so you stop leading on an empty tank.",
  },
  {
    icon: Ear,
    name: "The LOVIN Listening Scale",
    detail:
      "Measure the one skill that decides whether your team tells you the truth before it becomes a crisis.",
  },
];

const INCLUDED = [
  "Your full Built Index score with all 10 principle breakdowns",
  "Your DISC leadership profile and the built move for your style",
  "Your fuel and drain languages with a personalized action booklet",
  "Your LOVIN listening tier and where trust is leaking",
  "A personalized Built Letter written in Dr. Sully's voice",
  "A free digital copy of Built On Purpose",
];

const BuiltIndex = () => (
  <div className="min-h-screen bg-background text-foreground">
    <PageMeta
      title="The Built Index™ — Leadership Diagnostic · Dr. Sully Speaks"
      description={`Score your leadership across 10 MAKECHANGE principles, DISC style, fuel & drain, and listening. Free assessment. $${FULL_REPORT_PRICE_USD} full report includes a free copy of Built On Purpose.`}
      path="/built-index"
    />
    <SiteNav />

    <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-hero overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_18%_25%,hsl(var(--gold)/0.12),transparent_55%),radial-gradient(circle_at_82%_70%,hsl(var(--orange)/0.1),transparent_55%)]"
      />
      <div className="container relative grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
        <Reveal variant="up">
          <div className="inline-flex items-center gap-2 bg-orange/10 border border-orange/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-orange">
            <Clock className="h-3 w-3" /> 4 Assessments · ~12 Minutes · Free To Start
          </div>
          <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.02]">
            Is your leadership <span className="text-gradient-gold">built</span> — or just
            busy?
          </h1>
          <p className="text-silver text-base md:text-lg mt-6 max-w-xl leading-relaxed">
            The Built Index™ measures the structure underneath your results: your ten
            MAKECHANGE principles, your decision style, what fuels and drains you, and
            how well you actually listen. Most leaders do not have a strategy problem.
            They have a structure problem.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={BUILT_INDEX_ROUTES.start}
              className="inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition"
            >
              Take the Free Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#whats-inside"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 text-xs uppercase tracking-[0.2em] hover:border-gold/60 transition"
            >
              See what you get
            </a>
          </div>
          <p className="text-silver/70 text-xs mt-4">
            Your score is free. The full report and your free book are ${FULL_REPORT_PRICE_USD}.
          </p>
        </Reveal>

        <Reveal variant="scale" delay={120}>
          <ul className="grid gap-3">
            {ASSESSMENTS.map(({ icon: Icon, name, detail }) => (
              <li
                key={name}
                className="border border-white/10 bg-white/[0.04] p-5 flex gap-4"
              >
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 border border-gold/30 text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display uppercase text-white text-sm tracking-wide">
                    {name}
                  </p>
                  <p className="text-silver text-sm mt-1.5 leading-relaxed">{detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>

    <section className="py-16 md:py-24 bg-background">
      <div className="container">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          The Ten Principles
        </p>
        <h2 className="font-display font-bold uppercase text-3xl md:text-4xl mt-3">
          MAKECHANGE, scored honestly.
        </h2>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {BUILT_PRINCIPLES.map((p) => (
            <div key={p.key} className="border border-border p-4">
              <span className="font-display text-3xl text-orange">{p.letter}</span>
              <p className="font-display uppercase text-sm mt-1">{p.name}</p>
              <p className="text-muted-foreground text-xs mt-1.5 leading-relaxed">
                {p.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section id="whats-inside" className="py-16 md:py-24 bg-hero">
      <div className="container grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
            The Full Report
          </p>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-4xl mt-3">
            Everything the free score leaves on the table.
          </h2>
          <ul className="mt-8 space-y-3">
            {INCLUDED.map((item) => (
              <li key={item} className="flex gap-3 text-silver text-sm leading-relaxed">
                <CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-gold/30 bg-white/[0.04] p-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-orange">
            <BookOpen className="h-3.5 w-3.5" /> Founders Launch Pricing
          </div>
          <div className="mt-4 flex items-end gap-3">
            <span className="font-display text-5xl text-white">
              ${FULL_REPORT_PRICE_USD}
            </span>
            <span className="text-silver/60 line-through text-lg mb-1">
              ${ORIGINAL_REPORT_PRICE_USD}
            </span>
          </div>
          <p className="text-silver text-sm mt-4 leading-relaxed">
            One-time. Includes your personalized Built Letter and a free digital copy of
            <em> Built On Purpose</em>. No subscription, no upsell wall.
          </p>
          <Link
            to={BUILT_INDEX_ROUTES.start}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-orange text-primary-foreground px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition"
          >
            Start Free — Unlock Later <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="text-silver/60 text-[11px] mt-3 text-center">
            You only pay after you see your score.
          </p>
        </div>
      </div>
    </section>

    <ConversionBand
      eyebrow="After Your Report"
      heading="Turn the diagnosis into a plan."
      subheading="Leaders who want the structure rebuilt with them book a strategy session and walk out with a 90-day sequence."
      primaryLabel="Apply for a Strategy Session"
      primaryHref="/strategy-session"
      secondaryLabel="See Pricing"
      secondaryHref="/invest"
    />

    <SiteFooter />
  </div>
);

export default BuiltIndex;
